/**
 * Los datos del catalogo publico, y la consulta que junta el cliente.
 *
 * Estas son las UNICAS llamadas del sistema que no llevan sesion: pegan a
 * `/publico`, que sirve vistas sin costos ni margenes (migracion 0009). Por eso
 * no usan los hooks de `lib/queries.ts`, que asumen una sesion.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { api, type ErrorApi } from "@/lib/api";

import {
  agregar as agregarALista,
  quitar as quitarDeLista,
  sacar as sacarDeLista,
  type Consulta,
  type FotoDeModelo,
  type NegocioPublico,
  type OfertaPublica,
  type ProductoPublico,
} from "./logica";

export const clavesTienda = {
  raiz: ["tienda"] as const,
  productos: ["tienda", "productos"] as const,
  negocio: ["tienda", "negocio"] as const,
  fotosDeModelo: ["tienda", "fotos-de-modelo"] as const,
  ofertas: ["tienda", "ofertas"] as const,
};

/**
 * Todo el catalogo de una.
 *
 * Pedirlos todos y agrupar en el cliente evita cuatro pedidos, uno por
 * seccion, y deja el buscador andando sin ir al servidor por cada tecla. El
 * tope es el de la ruta, 500: con 200 —el de antes— un catalogo de 380 cortaba
 * los ultimos sin avisar, y el home (0093) no podia mostrarlos.
 */
export function useCatalogo(): UseQueryResult<ProductoPublico[], ErrorApi> {
  return useQuery<ProductoPublico[], ErrorApi>({
    queryKey: clavesTienda.productos,
    queryFn: async ({ signal }) => {
      const r = await api.get<{ filas: ProductoPublico[] }>("/publico/productos", {
        params: { limite: 500 },
        signal,
      });
      return r?.filas ?? [];
    },
    staleTime: 60_000,
  });
}

/**
 * Las ofertas de esta semana.
 *
 * Van en su propio pedido y no mezcladas con los productos porque no son
 * productos: no tienen stock ni entran al carrito. Cambian cuando el local las
 * carga, o sea cada varios dias, pero un minuto de cache alcanza: una oferta
 * vencida en pantalla es plata.
 */
export function useOfertas(): UseQueryResult<OfertaPublica[], ErrorApi> {
  return useQuery<OfertaPublica[], ErrorApi>({
    queryKey: clavesTienda.ofertas,
    queryFn: async ({ signal }) => {
      const r = await api.get<{ filas: OfertaPublica[] }>("/publico/ofertas", { signal });
      return r?.filas ?? [];
    },
    staleTime: 60_000,
  });
}

export function useNegocio(): UseQueryResult<NegocioPublico | null, ErrorApi> {
  return useQuery<NegocioPublico | null, ErrorApi>({
    queryKey: clavesTienda.negocio,
    queryFn: ({ signal }) => api.get<NegocioPublico | null>("/publico/negocio", { signal }),
    staleTime: 5 * 60_000,
  });
}

/**
 * Que modelo y color tienen foto.
 *
 * Se pide UNA vez para toda la pantalla y no una por ficha: son unas pocas
 * decenas de filas sin bytes, y el selector de color las necesita ANTES de
 * dibujarse. Un color que se puede tocar y no cambia nada se lee como que la
 * pagina esta rota, asi que conviene saberlo de antemano.
 *
 * Cambian cuando alguien sube una foto, o sea casi nunca: media hora de cache.
 */
export function useFotosDeModelo(): UseQueryResult<FotoDeModelo[], ErrorApi> {
  return useQuery<FotoDeModelo[], ErrorApi>({
    queryKey: clavesTienda.fotosDeModelo,
    queryFn: async ({ signal }) => {
      const r = await api.get<{ filas: FotoDeModelo[] }>("/publico/imagenes/modelos", { signal });
      return r?.filas ?? [];
    },
    staleTime: 30 * 60_000,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// La consulta, guardada en el telefono
// ─────────────────────────────────────────────────────────────────────────────

const CLAVE = "tora.consulta";

/**
 * Se guarda en `localStorage` y no en el servidor, por dos razones.
 *
 * La primera es que no hace falta: la consulta viaja por WhatsApp, asi que el
 * sistema no necesita saber nada hasta que llega el mensaje. La segunda es que
 * guardarla en el servidor obligaria a identificar a un visitante anonimo, que
 * es justo lo que el catalogo publico no hace.
 *
 * Todo lector y escritor va con try/catch: en una ventana privada de iOS el
 * acceso TIRA en vez de devolver vacio, y una excepcion ahi deja la pantalla en
 * blanco.
 */
function leerGuardada(): Consulta {
  try {
    const crudo = window.localStorage.getItem(CLAVE);
    if (!crudo) return [];
    const datos: unknown = JSON.parse(crudo);
    if (!Array.isArray(datos)) return [];
    // Se valida lo minimo: un localStorage viejo o tocado a mano no puede
    // romper la pantalla.
    return datos.filter(
      (l): l is Consulta[number] =>
        typeof l === "object" && l !== null && typeof (l as { id?: unknown }).id === "number",
    );
  } catch {
    return [];
  }
}

function guardar(consulta: Consulta): void {
  try {
    window.localStorage.setItem(CLAVE, JSON.stringify(consulta));
  } catch {
    // Sin espacio o sin permiso: la consulta sigue viva en memoria hasta que se
    // cierre la pestaña, que es mejor que romper.
  }
}

export interface UsoConsulta {
  consulta: Consulta;
  /** `color` es lo que eligio el cliente en la ficha, y viaja en el mensaje. */
  agregar: (p: ProductoPublico, color?: string | null) => void;
  quitar: (id: number) => void;
  sacar: (id: number) => void;
  vaciar: () => void;
}

export function useConsulta(): UsoConsulta {
  const [consulta, setConsulta] = useState<Consulta>(() =>
    typeof window === "undefined" ? [] : leerGuardada(),
  );

  useEffect(() => {
    guardar(consulta);
  }, [consulta]);

  return {
    consulta,
    agregar: useCallback(
      (p: ProductoPublico, color?: string | null) => setConsulta((c) => agregarALista(c, p, color)),
      [],
    ),
    quitar: useCallback((id: number) => setConsulta((c) => quitarDeLista(c, id)), []),
    sacar: useCallback((id: number) => setConsulta((c) => sacarDeLista(c, id)), []),
    vaciar: useCallback(() => setConsulta([]), []),
  };
}
